import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import Icon from 'react-native-vector-icons/Ionicons';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RESOURCES_COLORS } from '@/constants/residents/mobile/resourcesColors';
import { MOBILE_TYPOGRAPHY } from '@/constants/residents/mobile/mobileTypography';
import { WifiInfo } from '@/types/residents/wifi';
import { useToast } from '@/contexts/admin/ToastContext';
import { openExternalUrl } from '@/utils/common/linking';

// Shared components
import { PurpleBadge } from '@/components/residents/mobile/PurpleBadge';
import { CopyableInfoRow } from '@/components/residents/mobile/CopyableInfoRow';

const CBXLogo = RESIDENTS_SVG_COMPONENTS.CBX_LOGO;

interface MobileWifiSupportModalProps {
  visible: boolean;
  wifiInfo: WifiInfo | null;
  onClose: () => void;
}

/**
 * Mobile WiFi Support Modal
 * Shows CBX support contact information when user has WiFi issues
 * Matches Figma design for "CBX Having issues?" screen
 */
export const MobileWifiSupportModal: React.FC<MobileWifiSupportModalProps> = ({
  visible,
  wifiInfo,
  onClose,
}) => {
  const { showSuccess } = useToast();

  const handleCopyPhone = () => {
    if (wifiInfo?.supportPhoneNumber) {
      Clipboard.setString(wifiInfo.supportPhoneNumber);
      showSuccess('Phone number copied');
    }
  };

  const handleCopyEmail = () => {
    if (wifiInfo?.supportEmail) {
      Clipboard.setString(wifiInfo.supportEmail);
      showSuccess('Email copied');
    }
  };

  const handlePhonePress = async () => {
    if (wifiInfo?.supportPhoneNumber) {
      await openExternalUrl(`sms:${wifiInfo.supportPhoneNumber}`);
    }
  };

  const handleEmailPress = async () => {
    if (wifiInfo?.supportEmail) {
      await openExternalUrl(`mailto:${wifiInfo.supportEmail}`);
    }
  };

  if (!wifiInfo) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable style={styles.card} onPress={(e) => e.stopPropagation()}>
          {/* Top Row - Logo and Badge */}
          <View style={styles.topRow}>
            <CBXLogo width={68} height={29} />
            <PurpleBadge label="Wi-Fi network" />
          </View>

          {/* Title Section */}
          <View style={styles.titleSection}>
            <Text style={styles.title}>Having issues?</Text>
            <Text style={styles.subtitle}>Contact the CBX support team</Text>
          </View>

          {/* Contact Info Section */}
          <View style={styles.contactSection}>
            {/* Text/Phone */}
            {wifiInfo.supportPhoneNumber && (
              <CopyableInfoRow
                label="TEXT CBX"
                value={wifiInfo.supportPhoneNumber}
                onCopy={handleCopyPhone}
                onPress={handlePhonePress}
                labelColor={RESOURCES_COLORS.textMuted}
                valueColor={RESOURCES_COLORS.textPrimary}
              />
            )}

            {/* Email */}
            {wifiInfo.supportEmail && (
              <CopyableInfoRow
                label="EMAIL CBX"
                value={wifiInfo.supportEmail}
                onCopy={handleCopyEmail}
                onPress={handleEmailPress}
                labelColor={RESOURCES_COLORS.textMuted}
                valueColor={RESOURCES_COLORS.textPrimary}
              />
            )}
          </View>

          {/* Close Button */}
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <Icon name="close" size={24} color={RESOURCES_COLORS.textSecondary} />
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: RESOURCES_COLORS.cardBackground,
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    gap: 16,
    position: 'relative',
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  titleSection: {
    gap: 4,
  },
  title: {
    ...MOBILE_TYPOGRAPHY.title,
    color: RESOURCES_COLORS.textPrimary,
  },
  subtitle: {
    ...MOBILE_TYPOGRAPHY.body,
    color: RESOURCES_COLORS.textPrimary,
  },
  contactSection: {
    gap: 12,
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
